<template>
  <v-card outlined class="rounded-lg overflow-hidden">
    <v-tabs
      v-model="currentTab"
      background-color="grey lighten-4"
      :color="themeColor"
      dense
      :slider-color="themeColor"
    >
      <!-- 1. General y Precios (Solo en modo individual) -->
      <v-tab v-if="!compactModal" class="font-weight-bold text-caption">
        <v-icon left small>mdi-information-outline</v-icon>
        1. General y Precios
      </v-tab>
      <v-tab class="font-weight-bold text-caption">
        <v-icon left small>mdi-store</v-icon>
        {{ compactModal ? '1' : '2' }}. Deptos y Almacenes ({{ recipeDeptosRows.length }})
      </v-tab>
      <v-tab class="font-weight-bold text-caption">
        <v-icon left small>mdi-silverware-fork-knife</v-icon>
        {{ compactModal ? '2' : '3' }}. Insumos ({{ (recipe && recipe.Catalogo && recipe.Catalogo.length) || 0 }})
      </v-tab>
      <v-tab class="font-weight-bold text-caption">
        <v-icon left small>mdi-tune</v-icon>
        {{ compactModal ? '3' : '4' }}. Modificadores ({{ (recipe && recipe.Modificadores && recipe.Modificadores.length) || 0 }})
      </v-tab>
      <!-- 5. Canales y Descripción (Solo en modo individual) -->
      <v-tab v-if="!compactModal" class="font-weight-bold text-caption">
        <v-icon left small>mdi-cellphone-link</v-icon>
        5. Canales y Descripción
      </v-tab>
    </v-tabs>

    <v-tabs-items v-if="recipe" v-model="currentTab" class="pa-4">
      <!-- ================= TAB 1: GENERAL (Solo en modo individual) ================= -->
      <v-tab-item v-if="!compactModal">
        <v-row dense>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="recipe.Nombre"
              :label="mode === 'edit' ? 'Nombre del Producto / Receta *' : 'Nombre del Producto / Receta Replicada *'"
              outlined
              dense
              :error="!recipe.Nombre || recipe.Nombre.trim() === ''"
              :hint="mode === 'edit' ? 'Nombre del producto' : 'Nombre con el que se registrará el nuevo producto'"
              persistent-hint
            ></v-text-field>
            <small v-if="mode !== 'edit' && recipe.originalNombre" class="grey--text d-block mb-2">
              Origen: <em>{{ recipe.originalNombre }}</em>
            </small>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="recipe.Codigo"
              label="Código del Producto *"
              outlined
              dense
              :error="!recipe.Codigo || String(recipe.Codigo).trim() === ''"
              hint="Código único generado para el punto de venta"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="recipe.Categoria"
              :items="categories"
              item-value="id"
              item-text="Nombre"
              label="Categoría *"
              outlined
              dense
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="recipe.Precio"
              type="number"
              min="0"
              step="0.5"
              prefix="$"
              label="Precio de Venta *"
              outlined
              dense
              :hint="getPriceHint(recipe)"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="recipe.Moneda"
              :items="['MXN', 'USD']"
              label="Moneda"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="6">
            <v-menu
              v-model="menuVigencia"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="recipe.vaunAux"
                  label="Fecha de Vigencia"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hint="Después de esta fecha el item no será visible"
                  persistent-hint
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="recipe.vaunAux"
                @input="menuVigencia = false"
                no-title
                scrollable
                :color="themeColor"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="6">
            <v-combobox
              v-model="recipe.tags"
              :items="availableTags"
              label="Etiquetas / Tags"
              multiple
              small-chips
              deletable-chips
              outlined
              dense
              hint="Palabras clave para agrupar o buscar la receta"
              persistent-hint
            ></v-combobox>
          </v-col>
        </v-row>
      </v-tab-item>
      <!-- ================= TAB 1: DEPARTAMENTOS Y ALMACENES ================= -->
      <v-tab-item>
        <v-alert dense text type="info" class="mb-3 text-caption">
          <v-icon left small color="info">mdi-information-outline</v-icon>
          Puedes vincular departamentos a esta receta y asignar a cada uno su <strong>almacén / centro de consumo</strong> (conexión 1 a 1).
        </v-alert>

        <!-- Formulario para vincular departamento con almacén -->
        <v-card outlined class="pa-3 mb-3 rounded-lg grey lighten-5">
          <span :class="['text-subtitle-2 font-weight-bold d-block mb-2', themeTextClass]">
            <v-icon left small :color="themeColor">mdi-store-plus</v-icon>
            Vincular Departamento y Almacén:
          </span>
          <v-row dense align="center">
            <v-col cols="12" sm="5">
              <v-autocomplete
                v-model="newDepto.depto"
                :items="modulos"
                item-value="value"
                item-text="text"
                label="Departamento *"
                placeholder="Selecciona departamento..."
                outlined
                dense
                hide-details
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="5">
              <v-autocomplete
                v-model="newDepto.origen"
                :items="origenes"
                item-value="id"
                item-text="Nombre"
                label="Almacén / Centro de Consumo *"
                placeholder="Selecciona almacén..."
                clearable
                outlined
                dense
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn
                :color="themeColor"
                dark
                depressed
                block
                :disabled="!newDepto.depto || !newDepto.origen"
                @click="addDepto"
              >
                <v-icon left small>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Tabla de departamentos y almacenes asignados -->
        <v-card outlined class="rounded-lg overflow-hidden">
          <div class="pa-2 px-3 grey lighten-5 d-flex align-center justify-space-between">
            <span class="text-caption font-weight-bold grey--text text--darken-2">
              <v-icon left x-small :color="themeColor">mdi-format-list-checks</v-icon>
              Departamentos y almacenes vinculados a esta receta:
            </span>
            <v-chip
              x-small
              :color="recipeDeptosRows.length > 0 ? themeLightColor : 'red lighten-5'"
              :class="recipeDeptosRows.length > 0 ? `${themeTextClass} font-weight-bold` : 'red--text text--darken-2 font-weight-bold'"
            >
              {{ recipeDeptosRows.length }} departamento(s) vinculado(s)
            </v-chip>
          </div>
          <v-divider></v-divider>

          <div
            v-if="recipeDeptosRows.length === 0"
            class="pa-6 text-center grey--text"
          >
            <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-store-off</v-icon>
            <div class="text-body-2 font-weight-medium">Sin departamentos asignados</div>
            <div class="caption">Selecciona un departamento y su almacén arriba y haz clic en Agregar.</div>
          </div>

          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr class="grey lighten-4">
                  <th style="width: 45px">#</th>
                  <th style="width: 35%">Departamento</th>
                  <th>Almacén / Centro de Consumo</th>
                  <th class="text-center" style="width: 70px">Quitar</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(dep, dIdx) in recipeDeptosRows"
                  :key="dep.depto"
                >
                  <td class="grey--text font-weight-bold">{{ dIdx + 1 }}</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar size="24" :color="themeLightColor" class="mr-2">
                        <v-icon x-small :color="themeColor">mdi-store</v-icon>
                      </v-avatar>
                      <span class="font-weight-medium text-body-2">{{ dep.deptoName }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="py-1">
                      <v-autocomplete
                        :value="dep.origenes && dep.origenes.length > 0 ? dep.origenes[0] : null"
                        @input="updateRecipeDeptoOrigenes(recipe, dep.depto, $event ? [$event] : [])"
                        :items="origenes"
                        item-value="id"
                        item-text="Nombre"
                        placeholder="Seleccionar almacén..."
                        dense
                        outlined
                        hide-details
                        clearable
                      ></v-autocomplete>
                    </div>
                  </td>
                  <td class="text-center">
                    <v-btn
                      icon
                      small
                      color="red"
                      @click="removeDepto(dep.depto)"
                      title="Quitar este departamento"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-tab-item>

      <!-- ================= TAB 3: INSUMOS ================= -->
      <v-tab-item>
        <v-card outlined class="pa-3 mb-4 rounded-lg grey lighten-5">
          <span :class="['text-subtitle-2 font-weight-bold d-block mb-2', themeTextClass]">
            <v-icon left small :color="themeColor">mdi-plus-circle</v-icon>
            Agregar Insumo o Subreceta a la receta:
          </span>
          <v-row dense align="center">
            <v-col cols="12" sm="5">
              <v-autocomplete
                v-model="newInsumo.Id"
                :items="availableInsumos"
                item-value="id"
                item-text="label"
                label="Buscar insumo o subreceta..."
                dense
                outlined
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model.number="newInsumo.Cantidad"
                type="number"
                min="0.001"
                step="0.1"
                label="Cantidad"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model.number="newInsumo.Mm"
                type="number"
                min="0"
                max="100"
                label="Merma %"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-btn
                :color="themeColor"
                dark
                depressed
                block
                @click="addInsumo"
              >
                <v-icon left small>mdi-plus</v-icon>
                Agregar Insumo
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-subtitle-2 font-weight-bold">
            Ingredientes incluidos ({{ (recipe.Catalogo && recipe.Catalogo.length) || 0 }}):
          </span>
        </div>
        <v-card outlined class="rounded-lg overflow-hidden">
          <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr class="grey lighten-4">
                <th>Insumo / Subreceta</th>
                <th style="width: 80px" class="text-center">U.M.</th>
                <th style="width: 140px">Cantidad</th>
                <th style="width: 120px">Merma %</th>
                <th style="width: 60px" class="text-center">Quitar</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(ins, i) in recipe.Catalogo"
                :key="i"
              >
                <td class="font-weight-medium">
                  {{ ins.Nombre }}
                  <span class="caption grey--text d-block">{{ ins.Tipo }}</span>
                </td>
                <td class="text-center">
                  <v-chip x-small>{{
                    ins.UM || (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.UM) || "PZA"
                  }}</v-chip>
                </td>
                <td>
                  <v-text-field
                    v-model.number="ins.Cantidad"
                    type="number"
                    min="0.0001"
                    step="0.1"
                    dense
                    outlined
                    hide-details
                    class="my-1"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model.number="ins.Mm"
                    type="number"
                    min="0"
                    max="100"
                    dense
                    outlined
                    hide-details
                    class="my-1"
                  ></v-text-field>
                </td>
                <td class="text-center">
                  <v-btn
                    icon
                    x-small
                    color="red"
                    @click="removeInsumo(i)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!recipe.Catalogo || recipe.Catalogo.length === 0">
                <td colspan="5" class="text-center py-4 grey--text">
                  Esta receta actualmente no tiene insumos agregados.
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        </v-card>
      </v-tab-item>

      <!-- ================= TAB 4: MODIFICADORES ================= -->
      <v-tab-item>
        <v-alert dense text type="info" class="mb-3 text-caption">
          Los grupos de modificadores permiten al mesero o comensal personalizar el platillo (ej: Término de carne, Salsas, Guarnición).
        </v-alert>

        <v-card outlined class="pa-3 mb-4 rounded-lg grey lighten-5">
          <span :class="['text-subtitle-2 font-weight-bold d-block mb-2', themeTextClass]">
            <v-icon left small :color="themeColor">mdi-plus-circle</v-icon>
            Vincular Grupo de Modificadores:
          </span>
          <v-row dense align="center">
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="newModificador.modId"
                :items="allModificadores"
                item-value="id"
                item-text="label"
                label="Seleccionar Modificador *"
                dense
                outlined
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="newModificador.origenId"
                :items="origenes"
                item-value="id"
                item-text="Nombre"
                label="Centro Consumo (Opcional)"
                dense
                outlined
                hide-details
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn
                :color="themeColor"
                dark
                depressed
                block
                @click="addModificador"
              >
                <v-icon left small>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <span class="text-subtitle-2 font-weight-bold d-block mb-2">
          Modificadores vinculados ({{ (recipe.Modificadores && recipe.Modificadores.length) || 0 }}):
        </span>
        <v-card outlined class="rounded-lg overflow-hidden">
          <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr class="grey lighten-4">
                <th>Grupo de Modificadores</th>
                <th>Centro de Consumo Vinculado</th>
                <th style="width: 80px" class="text-center">Quitar</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(mod, mIdx) in recipe.Modificadores"
                :key="mIdx"
              >
                <td class="font-weight-medium">
                  <v-icon left small :color="themeColor">mdi-tune</v-icon>
                  {{ getModificadorName(mod.id) }}
                </td>
                <td>
                  <span v-if="mod.orn" :class="[themeTextClass, 'font-weight-medium']">
                    {{ getOrigenName(mod.orn) }}
                  </span>
                  <span v-else class="grey--text caption">Por defecto</span>
                </td>
                <td class="text-center">
                  <v-btn
                    icon
                    x-small
                    color="red"
                    @click="removeModificador(mIdx)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!recipe.Modificadores || recipe.Modificadores.length === 0">
                <td colspan="3" class="text-center py-4 grey--text">
                  No cuenta con modificadores vinculados.
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        </v-card>
      </v-tab-item>

      <!-- ================= TAB 5: CANALES Y DESCRIPCIÓN ================= -->
      <v-tab-item v-if="!compactModal">
        <v-row dense>
          <v-col cols="12" md="6">
            <span class="text-subtitle-2 font-weight-bold mb-2 d-block">Visibilidad en Canales:</span>
            <div class="pa-3 rounded-lg border-subtle mb-2">
              <v-switch
                v-model="recipe.opdv"
                label="Ocultar en Punto de Venta (PDV)"
                color="red darken-2"
                dense
                inset
                hide-details
              ></v-switch>
            </div>

            <div class="pa-3 rounded-lg border-subtle mb-2">
              <v-switch
                v-model="recipe.oweb"
                label="Ocultar en Menú Digital (Web)"
                color="red darken-2"
                dense
                inset
                hide-details
              ></v-switch>
            </div>

            <div class="pa-3 rounded-lg border-subtle">
              <v-switch
                v-model="recipe.ocmd"
                label="Ocultar en Comandero"
                color="red darken-2"
                dense
                inset
                hide-details
              ></v-switch>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <span class="text-subtitle-2 font-weight-bold mb-2 d-block">Descripción / Notas:</span>
            <v-textarea
              v-model="recipe.dscr"
              label="Descripción comercial o notas de cocina"
              outlined
              dense
              rows="7"
              hint="Texto visible para clientes o personal de cocina"
              persistent-hint
            ></v-textarea>
          </v-col>
        </v-row>
      </v-tab-item>

    </v-tabs-items>
  </v-card>
</template>

<script>
import ReplicadorRecetas, {
  resolveId,
  buildModulosMap,
  buildOrigenesMap,
  buildModificadoresMap,
  getDeptoName,
  getOrigenName,
  getModificadorName,
  getRecipeDeptosRows,
  getTotalAlmacenesCount,
} from "../../../clases/Restaurante/ReplicadorRecetas.js";

export default {
  name: "RecetaEditorTabs",
  props: {
    compactModal: {
      type: Boolean,
      default: false,
    },
    recipe: {
      type: Object,
      required: true,
    },
    value: {
      type: Number,
      default: 0,
    },
    mode: {
      type: String,
      default: "replicate", // 'replicate' | 'edit'
    },
    categories: {
      type: Array,
      default: () => [],
    },
    modulos: {
      type: Array,
      default: () => [],
    },
    origenes: {
      type: Array,
      default: () => [],
    },
    availableInsumos: {
      type: Array,
      default: () => [],
    },
    allModificadores: {
      type: Array,
      default: () => [],
    },
    availableTags: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      menuVigencia: false,
      newDepto: {
        depto: null,
        origen: null,
      },
      newInsumo: {
        Id: null,
        Cantidad: 1,
        Mm: 0,
      },
      newModificador: {
        modId: null,
        origenId: null,
      },
    };
  },
  computed: {
    currentTab: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    modulosMap() {
      return buildModulosMap(this.modulos);
    },
    origenesMap() {
      return buildOrigenesMap(this.origenes);
    },
    modificadoresMap() {
      return buildModificadoresMap(this.allModificadores);
    },
    themeColor() {
      return this.mode === "edit" ? "teal darken-1" : "primary";
    },
    themeLightColor() {
      return this.mode === "edit" ? "teal lighten-5" : "blue lighten-5";
    },
    themeTextClass() {
      return this.mode === "edit" ? "teal--text text--darken-1" : "primary--text";
    },
    recipeDeptosRows() {
      return this.recipe
        ? getRecipeDeptosRows(this.recipe, this.modulosMap, this.modulos)
        : [];
    },
    recipeTotalAlmacenes() {
      return this.recipe
        ? getTotalAlmacenesCount(this.recipe, this.modulosMap, this.modulos)
        : 0;
    },
  },
  methods: {
    getPriceDiffInfo(item) {
      return ReplicadorRecetas.getPriceDiffInfo(item);
    },
    getPriceHint(item) {
      const { hasChanged, diff, percentDiff, orig } = this.getPriceDiffInfo(item);
      if (!hasChanged) return "Sin cambios respecto al precio original";
      const sign = diff > 0 ? "+" : "";
      return `Base: $${orig.toFixed(2)} (${sign}$${diff.toFixed(2)} / ${percentDiff}%)`;
    },
    getDeptoName(deptoId) {
      return getDeptoName(deptoId, this.modulosMap, this.modulos);
    },
    getOrigenName(origenId) {
      return getOrigenName(origenId, this.origenesMap, this.origenes);
    },
    getModificadorName(id) {
      return getModificadorName(id, this.modificadoresMap);
    },
    getRecipeDeptosRows(recipe) {
      return getRecipeDeptosRows(recipe, this.modulosMap, this.modulos);
    },
    getTotalAlmacenesCount(recipe) {
      return getTotalAlmacenesCount(recipe, this.modulosMap, this.modulos);
    },
    addDepto() {
      if (!this.recipe || !this.newDepto.depto || !this.newDepto.origen) return;
      const dKey = String(this.newDepto.depto).trim();
      const origenes = [this.newDepto.origen];

      if (!this.recipe.CCs || typeof this.recipe.CCs !== "object") {
        this.$set(this.recipe, "CCs", {});
      }
      this.$set(this.recipe.CCs, dKey, origenes);
      this.recipe.Departamentos = Object.keys(this.recipe.CCs);
      if (!this.recipe.selectedDepto) {
        this.recipe.selectedDepto = dKey;
        this.recipe.selectedOrigen = [...origenes];
      }

      this.newDepto = { depto: null, origen: null };
    },
    removeDepto(deptoKey) {
      if (!this.recipe || !this.recipe.CCs) return;
      this.$delete(this.recipe.CCs, deptoKey);
      this.recipe.Departamentos = Object.keys(this.recipe.CCs);
      if (this.recipe.selectedDepto === deptoKey) {
        const first = this.recipe.Departamentos[0] || null;
        this.recipe.selectedDepto = first;
        this.recipe.selectedOrigen =
          first && this.recipe.CCs[first] ? [...this.recipe.CCs[first]] : [];
      }
    },
    updateRecipeDeptoOrigenes(recipe, deptoKey, newOrigenes) {
      if (!recipe) return;
      if (!recipe.CCs || typeof recipe.CCs !== "object") {
        this.$set(recipe, "CCs", {});
      }
      const arr = Array.isArray(newOrigenes) ? [...newOrigenes] : [];
      this.$set(recipe.CCs, deptoKey, arr);
      if (recipe.selectedDepto === deptoKey) {
        recipe.selectedOrigen = [...arr];
      }
    },
    addInsumo() {
      if (!this.recipe || !this.newInsumo.Id) {
        this.$emit("notify", { message: "Selecciona un insumo antes de agregar", type: "warning" });
        return;
      }
      const insId = this.newInsumo.Id;
      if (!Array.isArray(this.recipe.Catalogo)) {
        this.$set(this.recipe, "Catalogo", []);
      }

      const existing = this.recipe.Catalogo.find(
        (ins) => (ins.Id || ins.id) === insId
      );
      if (existing) {
        existing.Cantidad =
          Number(existing.Cantidad || 0) + Number(this.newInsumo.Cantidad || 1);
        this.$emit("notify", { message: "Se incrementó la cantidad del insumo en la receta", type: "info" });
        this.newInsumo = { Id: null, Cantidad: 1, Mm: 0 };
        return;
      }

      const found = this.availableInsumos.find(
        (p) => (p.Id || p.id) === insId
      );
      const name = found ? found.Nombre || "" : "";
      const um = found ? found.UM || "PZA" : "PZA";
      const tipo = found ? found.Tipo || "AYB-Insumo" : "AYB-Insumo";

      this.recipe.Catalogo.push({
        Id: insId,
        id: insId,
        Nombre: name,
        Tipo: tipo,
        Cantidad: Number(this.newInsumo.Cantidad) || 1,
        Mm: Number(this.newInsumo.Mm) || 0,
        UM: um,
        Moneda: this.recipe.Moneda || "MXN",
        Catalogo: found && Array.isArray(found.Catalogo) ? [...found.Catalogo] : [],
        Extra: {
          AYB: {
            UM: um,
            Mm: Number(this.newInsumo.Mm) || 0,
            Rm: 1,
          },
        },
      });

      this.newInsumo = { Id: null, Cantidad: 1, Mm: 0 };
    },
    removeInsumo(index) {
      if (this.recipe && Array.isArray(this.recipe.Catalogo)) {
        this.recipe.Catalogo.splice(index, 1);
      }
    },
    addModificador() {
      if (!this.recipe || !this.newModificador.modId) {
        this.$emit("notify", { message: "Selecciona un modificador antes de agregar", type: "warning" });
        return;
      }
      if (!Array.isArray(this.recipe.Modificadores)) {
        this.$set(this.recipe, "Modificadores", []);
      }
      const modId = this.newModificador.modId;
      const orn = this.newModificador.origenId || null;

      const exists = this.recipe.Modificadores.some(
        (m) => resolveId(m) === modId && (m.orn || null) === orn
      );
      if (exists) {
        this.$emit("notify", { message: "Este modificador ya está vinculado con ese centro de consumo", type: "warning" });
        return;
      }

      this.recipe.Modificadores.push({
        id: modId,
        Id: modId,
        orn: orn,
      });

      this.newModificador = { modId: null, origenId: null };
    },
    removeModificador(index) {
      if (this.recipe && Array.isArray(this.recipe.Modificadores)) {
        this.recipe.Modificadores.splice(index, 1);
      }
    },
  },
};
</script>

